/**
 * Read-tier per-document grouping (#20423 close-gaps, mockup parity).
 *
 * The Read tier lists DOCUMENTS (not individual passages): each row shows
 * "N passages retrieved · M cited", where N counts every distinct passage of
 * that document pulled into context (read + cited tiers — cited passages were
 * retrieved too) and M counts the ones actually cited. M = 0 renders a
 * "not cited" pill. Pure module so the selector is unit-testable.
 */
import type { ProvenanceSource } from "../../types/provenance";
import { titleOf } from "./crossReferences";

/** One Read-tier row: a document with its retrieval/citation passage counts. */
export interface ReadDocumentGroup {
  /** Stable per-document key (SDR id when present, else the display title). */
  docKey: string;
  title: string;
  /** Distinct passages of this document retrieved into context (read + cited). */
  passagesRetrieved: number;
  /** Distinct passages of this document the response cited. */
  passagesCited: number;
  /** The document's read-tier sources, payload order — [0] is the row's
   * representative for click-to-open (annotations gather the whole doc). */
  sources: ProvenanceSource[];
}

/** Per-document key: SDR id when present, else the display title (so sources
 * with no SDR still group deterministically instead of colliding on ""). */
const docKeyOf = (source: ProvenanceSource): string => {
  const sdrId = source.source_data_record?.id;
  return sdrId != null ? `sdr-${sdrId}` : `title-${titleOf(source)}`;
};

/** Distinct-chunk counter (a chunk retrieved by several events still counts
 * once). Chunkless entries fall back to per-entry uniqueness via `fallback`. */
const addChunk = (
  seen: Set<string>,
  source: ProvenanceSource,
  fallback: string,
) => {
  const chunkId = source.data_chunk?.id;
  seen.add(chunkId != null ? `chunk-${chunkId}` : fallback);
};

/**
 * Group the Read tier by document, counting retrieved vs cited passages from
 * both tiers. Documents keep the order of their first appearance in
 * `readSources`; documents present only in the Cited tier are NOT returned
 * (they have no read-tier row — the Cited tier already shows them).
 */
export const groupReadByDocument = (
  readSources: ProvenanceSource[],
  citedSources: ProvenanceSource[],
): ReadDocumentGroup[] => {
  const groups = new Map<
    string,
    ReadDocumentGroup & {
      retrievedChunks: Set<string>;
      citedChunks: Set<string>;
    }
  >();

  readSources.forEach((source, i) => {
    const docKey = docKeyOf(source);
    let group = groups.get(docKey);
    if (!group) {
      group = {
        docKey,
        title: titleOf(source),
        passagesRetrieved: 0,
        passagesCited: 0,
        sources: [],
        retrievedChunks: new Set<string>(),
        citedChunks: new Set<string>(),
      };
      groups.set(docKey, group);
    }
    group.sources.push(source);
    addChunk(group.retrievedChunks, source, `read-${i}`);
  });

  // Cited passages of the same documents count toward BOTH totals: they were
  // retrieved (funnel-in) and cited (funnel-out).
  citedSources.forEach((source, i) => {
    const group = groups.get(docKeyOf(source));
    if (!group) return;
    addChunk(group.retrievedChunks, source, `cited-${i}`);
    addChunk(group.citedChunks, source, `cited-${i}`);
  });

  return [...groups.values()].map(
    ({ retrievedChunks, citedChunks, ...group }) => ({
      ...group,
      passagesRetrieved: retrievedChunks.size,
      passagesCited: citedChunks.size,
    }),
  );
};
